import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertCircle, ArrowRightLeft, CalendarDays, ChevronDown, ChevronRight, ExternalLink, FileText, History, RefreshCw,
  Scissors, Sparkles, Upload, UserRound,
} from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import {
  useGetMaintenanceBreakdown, useGetMaintenanceInvoiceUrl, useGetMaintenanceLineDriverOptions,
  useMoveMaintenanceLine, useReadMaintenanceInvoice, useResolveMaintenanceInvoiceItem,
  useSetMaintenanceLineBorne, useSetMaintenanceLineDate, useSplitMaintenanceLine, useUploadMaintenanceInvoice,
} from '../../hooks/backend/fleet'
import { writeErrorMessage } from '../../utils/writeError'
import { fileToBase64 } from '../../utils/marketing'
import {
  BORNE_TONE, MAINTENANCE_CATEGORIES, borneLabel, categoryLabel, monthLabel, monthStart, readerDisagrees, zarExact,
  type BorneBy, type Breakdown, type BreakdownInvoice, type BreakdownLine, type InvoiceItem,
} from '../../utils/servicing'

/** The cell that was clicked. */
export type BreakdownFilter = {
  title: string
  vehicle_id?: string | null
  period_from: string
  period_to: string
  category?: string | null
  borne_by?: BorneBy | null
}

/**
 * What makes up a figure on the servicing page.
 *
 * Every number here comes from maintenance_breakdown_v1, the same lines the
 * month view sums, so the total at the top of this dialog is the figure that
 * was clicked. Nothing is added up in React beyond that one sum of the rows
 * already on screen, which is a display total, not a calculation of what
 * anyone owes (CLAUDE.md rule 4).
 *
 * The invoice reader's suggestion always sits BESIDE the category a person
 * chose and never replaces it. A suggestion becomes a change only when someone
 * presses Move, Split or Record, and the reason travels into the change log.
 */
export function MaintenanceBreakdownDialog({
  filter, canEdit, onOpenChange,
}: {
  filter: BreakdownFilter | null
  canEdit: boolean
  onOpenChange: (open: boolean) => void
}) {
  const breakdown = useGetMaintenanceBreakdown<Breakdown>()
  const load = breakdown.trigger
  const reload = useCallback(() => {
    if (!filter) return
    void load({
      vehicle_id: filter.vehicle_id ?? null,
      period_from: monthStart(filter.period_from),
      period_to: monthStart(filter.period_to),
      category: filter.category ?? null,
      borne_by: filter.borne_by ?? null,
    }, { skipCache: true }).catch(() => undefined)
  }, [filter, load])
  useEffect(() => { reload() }, [reload])

  const data = breakdown.data
  const lines = data?.lines ?? []
  const invoices = data?.invoices ?? []
  const total = lines.reduce((s, l) => s + Number(l.total_incl_vat_zar ?? 0), 0)
  const exVat = lines.reduce((s, l) => s + Number(l.amount_ex_vat_zar ?? 0), 0)
  const singleCarMonth = Boolean(filter?.vehicle_id) && monthStart(filter?.period_from) === monthStart(filter?.period_to)
  const unnamed = lines.filter((l) => l.borne_by === 'driver' && !l.driver_id).length
  const issued = lines.filter((l) => l.statement_issued).length

  return (
    <Dialog open={filter !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{filter?.title ?? ''}</DialogTitle>
          <DialogDescription>
            The lines that make up this figure, the invoices tied to them, and every change made to them.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Chip tone="neutral">{lines.length} line{lines.length === 1 ? '' : 's'}</Chip>
          <Chip tone="neutral">Ex VAT <span className="ml-1 font-semibold tabular-nums">{zarExact(exVat)}</span></Chip>
          <Chip tone="brand">Invoiced <span className="ml-1 font-semibold tabular-nums">{zarExact(total)}</span></Chip>
          <Chip tone="neutral">{invoices.length} invoice{invoices.length === 1 ? '' : 's'}</Chip>
          {unnamed > 0 && (
            <Chip tone="red">{unnamed} driver borne line{unnamed === 1 ? '' : 's'} with no driver named</Chip>
          )}
          <Button variant="ghost" size="sm" className="ml-auto h-7 gap-1 px-2 text-xs" onClick={reload}
            disabled={breakdown.loading}>
            <RefreshCw className={cn('h-3.5 w-3.5', breakdown.loading && 'animate-spin')} /> Reload
          </Button>
        </div>

        {issued > 0 && (
          <p className="rounded-md border border-amber-300/60 bg-amber-50 p-2 text-[11px] text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/30 dark:text-amber-200">
            {issued === lines.length ? 'Every line here is' : `${issued} of these lines are`} in a month whose owner
            statement has already been issued. Moving a line or changing who pays is allowed and recorded, but it does
            not alter an issued statement: the correction is carried forward to a later one.
          </p>
        )}

        {breakdown.error ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{breakdown.error}</span>
          </div>
        ) : breakdown.loading && !data ? (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading the lines…
          </div>
        ) : (
          <>
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Lines</h3>
              {lines.length === 0 ? (
                <p className="rounded-md border border-border p-3 text-xs text-muted-foreground">
                  No lines make up this figure.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full min-w-[900px] text-xs">
                    <thead>
                      <tr className="border-b border-border bg-muted text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="w-6 px-2 py-1.5" />
                        <th className="px-2 py-1.5 text-left font-semibold">{singleCarMonth ? 'Date' : 'Car and month'}</th>
                        <th className="px-2 py-1.5 text-left font-semibold">Category</th>
                        <th className="px-2 py-1.5 text-left font-semibold">What was done</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Ex VAT</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Invoiced</th>
                        <th className="px-2 py-1.5 text-left font-semibold">Who pays</th>
                        <th className="px-2 py-1.5 text-left font-semibold">Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l) => (
                        <LineRow key={l.id} line={l} canEdit={canEdit} singleCarMonth={singleCarMonth} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="space-y-2">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground">Invoices</h3>
                {!singleCarMonth && (
                  <span className="text-[11px] text-muted-foreground">
                    Open one car and one month to file an invoice against it.
                  </span>
                )}
              </div>
              {canEdit && singleCarMonth && filter?.vehicle_id && (
                <InvoiceUpload vehicleId={filter.vehicle_id} period={monthStart(filter.period_from) ?? filter.period_from} lines={lines}
                  defaultCategory={filter.category ?? null} />
              )}
              {invoices.length === 0 ? (
                <p className="rounded-md border border-border p-3 text-xs text-muted-foreground">
                  No invoice is tied to these lines yet. The 321 lines carried over from the servicing
                  workbook have none; an invoice filed here is the first.
                </p>
              ) : (
                invoices.map((inv) => <InvoiceCard key={inv.id} invoice={inv} lines={lines} canEdit={canEdit} />)
              )}
            </section>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// One line, with its actions folded underneath
// ---------------------------------------------------------------------------

type Editor = 'move' | 'split' | 'borne' | 'date' | 'history' | null

function LineRow({ line, canEdit, singleCarMonth }: { line: BreakdownLine; canEdit: boolean; singleCarMonth: boolean }) {
  const [editor, setEditor] = useState<Editor>(null)
  const invoiceUrl = useGetMaintenanceInvoiceUrl<{ url: string }>()
  const toggle = (e: Editor) => setEditor((cur) => (cur === e ? null : e))
  const driverMissing = line.borne_by === 'driver' && !line.driver_id

  async function openInvoice() {
    if (!line.invoice_id) return
    try {
      const r = await invoiceUrl.trigger({ invoice_id: line.invoice_id }, { skipCache: true }).result
      window.open(r.url, '_blank', 'noopener')
    } catch (err) {
      toast.error('Could not open the invoice', { description: writeErrorMessage(err) })
    }
  }

  return (
    <>
      <tr className={cn('border-b border-border/60 align-top', editor && 'bg-muted/40')}>
        <td className="px-2 py-1.5">
          {line.changes.length > 0 && (
            <button type="button" onClick={() => toggle('history')} title="Changes to this line"
              className="text-muted-foreground hover:text-foreground">
              {editor === 'history' ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          )}
        </td>
        <td className="whitespace-nowrap px-2 py-1.5 text-foreground">
          {singleCarMonth
            ? (line.incurred_on ?? monthLabel(line.period_start))
            : <><span className="font-mono font-semibold uppercase">{line.registration_number}</span>{' '}
                <span className="text-muted-foreground">{monthLabel(line.period_start)}</span></>}
        </td>
        <td className="whitespace-nowrap px-2 py-1.5 text-foreground">{categoryLabel(line.category)}</td>
        <td className="max-w-[260px] px-2 py-1.5 text-muted-foreground">
          <div className="line-clamp-2">{line.description ?? '—'}</div>
          <div className="text-[10px] text-muted-foreground/80">
            {line.source === 'sheet_v3' ? 'From the servicing workbook'
              : line.source === 'cash_repair' ? `Cash repair ${line.source_ref ?? ''}`
              : line.source === 'invoice' ? 'From an invoice'
              : line.created_by_name ? `Recorded by ${line.created_by_name}` : line.source}
          </div>
        </td>
        <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums text-foreground">{zarExact(line.amount_ex_vat_zar)}</td>
        <td className="whitespace-nowrap px-2 py-1.5 text-right font-semibold tabular-nums text-foreground">{zarExact(line.total_incl_vat_zar)}</td>
        <td className="px-2 py-1.5">
          <div className="flex flex-wrap items-center gap-1">
            {line.borne_by ? (
              <Chip tone={BORNE_TONE[line.borne_by]}>{borneLabel(line.borne_by)}</Chip>
            ) : <Chip tone="neutral">No rule</Chip>}
            {line.borne_by_override && <span className="text-[10px] text-muted-foreground" title={line.borne_reason ?? ''}>set by hand</span>}
          </div>
          {line.borne_by === 'driver' && (
            <div className="mt-0.5 text-[11px]">
              {driverMissing
                ? <span className="font-medium text-rose-700 dark:text-rose-300">Driver not named</span>
                : <span className="text-foreground">{line.driver_name}</span>}
              {line.driver_repair_case_ref && (
                <span className="ml-1 text-muted-foreground">
                  case {line.driver_repair_case_ref}, {(line.driver_repair_case_status ?? '').replace(/_/g, ' ')}
                </span>
              )}
            </div>
          )}
        </td>
        <td className="whitespace-nowrap px-2 py-1.5">
          {line.invoice_id ? (
            <button type="button" onClick={openInvoice} className="inline-flex items-center gap-1 text-[hsl(var(--brand))] hover:underline">
              <FileText className="h-3.5 w-3.5" /> Open
            </button>
          ) : <span className="text-muted-foreground/60">None</span>}
        </td>
      </tr>
      {canEdit && (
        <tr className="border-b border-border/60">
          <td />
          <td colSpan={7} className="px-2 pb-1.5">
            <div className="flex flex-wrap gap-1">
              <Button size="sm" variant={editor === 'move' ? 'secondary' : 'ghost'} className="h-6 gap-1 px-2 text-[11px]" onClick={() => toggle('move')}>
                <ArrowRightLeft className="h-3 w-3" /> Move category
              </Button>
              <Button size="sm" variant={editor === 'split' ? 'secondary' : 'ghost'} className="h-6 gap-1 px-2 text-[11px]" onClick={() => toggle('split')}>
                <Scissors className="h-3 w-3" /> Split part out
              </Button>
              <Button size="sm" variant={editor === 'date' ? 'secondary' : 'ghost'} className="h-6 gap-1 px-2 text-[11px]" onClick={() => toggle('date')}>
                <CalendarDays className="h-3 w-3" /> Change date
              </Button>
              <Button size="sm" variant={editor === 'borne' || driverMissing ? 'secondary' : 'ghost'}
                className={cn('h-6 gap-1 px-2 text-[11px]', driverMissing && 'text-rose-700 dark:text-rose-300')}
                onClick={() => toggle('borne')}>
                <UserRound className="h-3 w-3" /> {driverMissing ? 'Name the driver' : 'Who pays'}
              </Button>
              {line.statement_issued && (
                <span className="ml-1 self-center text-[10px] text-muted-foreground" title="The owner statement for this car and month has been issued. A change here does not alter it; carry the correction forward.">
                  statement issued
                </span>
              )}
            </div>
            {editor === 'move' && <MoveEditor line={line} onDone={(needDriver) => setEditor(needDriver ? 'borne' : null)} />}
            {editor === 'split' && <SplitEditor line={line} onDone={() => setEditor(null)} />}
            {editor === 'borne' && <BorneEditor line={line} onDone={() => setEditor(null)} />}
            {editor === 'date' && <DateEditor line={line} onDone={() => setEditor(null)} />}
          </td>
        </tr>
      )}
      {editor === 'history' && (
        <tr className="border-b border-border/60 bg-muted/30">
          <td />
          <td colSpan={7} className="px-2 py-2">
            <div className="flex items-center gap-1 text-[11px] font-semibold text-foreground"><History className="h-3 w-3" /> Changes</div>
            <ul className="mt-1 space-y-1">
              {line.changes.map((c, i) => (
                <li key={i} className="text-[11px] text-muted-foreground">
                  <span className="text-foreground">{new Date(c.at).toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg' })}</span>{' '}
                  {c.kind === 'category'
                    ? <>moved from {categoryLabel(c.from)} to {categoryLabel(c.to)}</>
                    : c.kind === 'date'
                    ? <>date and month changed from {c.from} to {c.to}</>
                    : <>who pays changed from {c.from === 'rule' ? 'the category rule' : borneLabel(c.from)} to {c.to === 'rule' ? 'the category rule' : borneLabel(c.to)}</>}
                  {c.from_borne_by !== c.to_borne_by && <> ({borneLabel(c.from_borne_by)} to {borneLabel(c.to_borne_by)})</>}
                  {' '}by {c.actor_email ?? 'unknown'}: “{c.reason}”
                  {c.statement_issued && <span className="ml-1 text-amber-700 dark:text-amber-300">after the statement was issued</span>}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  )
}

function CategorySelect({ id, value, onChange, exclude }: { id: string; value: string; onChange: (v: string) => void; exclude?: string }) {
  return (
    <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)} className="h-8 w-[190px] text-xs">
      <option value="">Choose a category</option>
      {MAINTENANCE_CATEGORIES.filter((c) => c.code !== exclude).map((c) => (
        <option key={c.code} value={c.code}>{c.label}</option>
      ))}
    </NativeSelect>
  )
}

function MoveEditor({ line, onDone }: { line: BreakdownLine; onDone: (needDriver: boolean) => void }) {
  const [to, setTo] = useState('')
  const [reason, setReason] = useState('')
  const move = useMoveMaintenanceLine<{ driver_needed?: boolean; borne_by?: string; statement_issued?: boolean }>()
  async function save() {
    if (!to) { toast.error('Choose the category to move it to'); return }
    try {
      const r = await move.trigger({ line_id: line.id, to_category: to, reason }).result
      toast.success(`Moved to ${categoryLabel(to)}`, {
        description: [
          `Who pays is now ${borneLabel(r?.borne_by)}.`,
          r?.driver_needed ? 'Name the driver next, which opens their repair case.' : null,
          r?.statement_issued ? 'The issued owner statement for this month is unchanged.' : null,
        ].filter(Boolean).join(' '),
      })
      onDone(Boolean(r?.driver_needed))
    } catch (err) {
      toast.error('Could not move the line', { description: writeErrorMessage(err) })
    }
  }
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2 rounded-md border border-border bg-background p-2">
      <div className="space-y-1">
        <Label htmlFor={`mv-${line.id}`} className="text-[11px]">Move the whole line, {zarExact(line.total_incl_vat_zar)}, to</Label>
        <CategorySelect id={`mv-${line.id}`} value={to} onChange={setTo} exclude={line.category} />
      </div>
      <div className="min-w-[260px] flex-1 space-y-1">
        <Label htmlFor={`mvr-${line.id}`} className="text-[11px]">Why</Label>
        <Input id={`mvr-${line.id}`} className="h-8 text-xs" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="The invoice is for a rear bumper respray, which is panel beating" />
      </div>
      <Button size="sm" className="h-8" onClick={save} disabled={move.loading}>
        {move.loading && <Spinner className="mr-1 h-3.5 w-3.5" />} Move
      </Button>
    </div>
  )
}

/**
 * The day the work was done and the month the line counts in. They can differ
 * (an invoice paid in October for September's work), so the month follows the
 * date only until somebody sets the month themselves.
 */
function DateEditor({ line, onDone }: { line: BreakdownLine; onDone: () => void }) {
  const startMonth = (monthStart(line.period_start) ?? '').slice(0, 7)
  const [day, setDay] = useState((line.incurred_on ?? '').slice(0, 10))
  const [month, setMonth] = useState(startMonth)
  const [monthTouched, setMonthTouched] = useState(false)
  const [reason, setReason] = useState('')
  const set = useSetMaintenanceLineDate<{ month_changed?: boolean; statement_issued?: boolean }>()

  async function save() {
    if (!month) { toast.error('Choose the month the line counts in'); return }
    try {
      const r = await set.trigger({ line_id: line.id, incurred_on: day || null, period_start: `${month}-01`, reason }).result
      toast.success(r?.month_changed ? `Moved to ${monthLabel(`${month}-01`)}` : 'Date changed', {
        description: r?.statement_issued
          ? 'An owner statement for the old or new month is already issued; it is not changed. Carry the correction forward.'
          : undefined,
      })
      onDone()
    } catch (err) {
      toast.error('Could not change the date', { description: writeErrorMessage(err) })
    }
  }

  return (
    <div className="mt-2 flex flex-wrap items-end gap-2 rounded-md border border-border bg-background p-2">
      <div className="space-y-1">
        <Label htmlFor={`dt-${line.id}`} className="text-[11px]">Date of the work or invoice</Label>
        <Input id={`dt-${line.id}`} type="date" className="h-8 w-[160px] text-xs" value={day}
          onChange={(e) => {
            setDay(e.target.value)
            if (!monthTouched && e.target.value) setMonth(e.target.value.slice(0, 7))
          }} />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`dm-${line.id}`} className="text-[11px]">Counts in the month of</Label>
        <Input id={`dm-${line.id}`} type="month" className="h-8 w-[160px] text-xs" value={month}
          onChange={(e) => { setMonth(e.target.value); setMonthTouched(true) }} />
      </div>
      <div className="min-w-[240px] flex-1 space-y-1">
        <Label htmlFor={`dr-${line.id}`} className="text-[11px]">Why</Label>
        <Input id={`dr-${line.id}`} className="h-8 text-xs" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Invoice dated 14 September, paid in October" />
      </div>
      <Button size="sm" className="h-8" onClick={save} disabled={set.loading}>
        {set.loading && <Spinner className="mr-1 h-3.5 w-3.5" />} Save
      </Button>
      {month && month !== startMonth && (
        <p className="basis-full text-[11px] text-muted-foreground">
          The line moves from {monthLabel(`${startMonth}-01`)} to {monthLabel(`${month}-01`)}, so it leaves one month's
          figures and joins the other's.
        </p>
      )}
    </div>
  )
}

function SplitEditor({ line, onDone }: { line: BreakdownLine; onDone: () => void }) {
  const [to, setTo] = useState('')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const split = useSplitMaintenanceLine()
  async function save() {
    const n = Number(amount)
    if (!to) { toast.error('Choose the category for the part'); return }
    if (!Number.isFinite(n) || n <= 0) { toast.error('Enter the part to move, excluding VAT'); return }
    try {
      await split.trigger({ line_id: line.id, amount_ex_vat_zar: n, to_category: to, reason }).result
      toast.success(`R${n.toFixed(2)} moved to ${categoryLabel(to)}`, {
        description: 'VAT followed it in the line’s own proportion, and the two lines still add up to the original.',
      })
      onDone()
    } catch (err) {
      toast.error('Could not split the line', { description: writeErrorMessage(err) })
    }
  }
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2 rounded-md border border-border bg-background p-2">
      <div className="space-y-1">
        <Label htmlFor={`sp-${line.id}`} className="text-[11px]">Part, excluding VAT (of {zarExact(line.amount_ex_vat_zar)})</Label>
        <Input id={`sp-${line.id}`} className="h-8 w-[150px] text-xs" type="number" inputMode="decimal" value={amount}
          onChange={(e) => setAmount(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`spc-${line.id}`} className="text-[11px]">Into</Label>
        <CategorySelect id={`spc-${line.id}`} value={to} onChange={setTo} exclude={line.category} />
      </div>
      <div className="min-w-[240px] flex-1 space-y-1">
        <Label htmlFor={`spr-${line.id}`} className="text-[11px]">Why</Label>
        <Input id={`spr-${line.id}`} className="h-8 text-xs" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="R1,200 of this line was a side mirror" />
      </div>
      <Button size="sm" className="h-8" onClick={save} disabled={split.loading}>
        {split.loading && <Spinner className="mr-1 h-3.5 w-3.5" />} Split
      </Button>
    </div>
  )
}

type DriverOption = { driver_id: string; driver_name: string | null; shifts: number; on_the_day: boolean; last_shift: string | null }

function BorneEditor({ line, onDone }: { line: BreakdownLine; onDone: () => void }) {
  const [borne, setBorne] = useState<string>(line.borne_by_override ?? (line.borne_by === 'driver' ? 'rule' : 'rule'))
  const [driverId, setDriverId] = useState(line.driver_id ?? '')
  const [reason, setReason] = useState('')
  const options = useGetMaintenanceLineDriverOptions<DriverOption[]>()
  const set = useSetMaintenanceLineBorne<{ driver_repair_case_id?: string | null; borne_by?: string }>()
  const loadDrivers = options.trigger
  // Driver borne either by hand or because the category says so.
  const willBeDriver = borne === 'driver' || (borne === 'rule' && line.borne_by === 'driver' && !line.borne_by_override)
  useEffect(() => {
    if (willBeDriver) void loadDrivers({ line_id: line.id }, { skipCache: true }).catch(() => undefined)
  }, [willBeDriver, loadDrivers, line.id])
  const drivers = options.data ?? []

  async function save() {
    try {
      const r = await set.trigger({
        line_id: line.id, borne_by: borne, driver_id: willBeDriver ? (driverId || null) : null, reason,
      }).result
      toast.success(`Who pays: ${borneLabel(r?.borne_by)}`, {
        description: r?.driver_repair_case_id
          ? 'A driver repair case is open for this. Nothing is deducted until fault, a fair procedure and a signed agreement are on record.'
          : undefined,
      })
      onDone()
    } catch (err) {
      toast.error('Could not change who pays', { description: writeErrorMessage(err) })
    }
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border border-border bg-background p-2">
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor={`bb-${line.id}`} className="text-[11px]">Who pays</Label>
          <NativeSelect id={`bb-${line.id}`} className="h-8 w-[230px] text-xs" value={borne} onChange={(e) => setBorne(e.target.value)}>
            <option value="rule">Let the category decide ({categoryLabel(line.category)})</option>
            <option value="owner">Owner</option>
            <option value="driver">Driver</option>
            <option value="fleetly">Fleetly</option>
          </NativeSelect>
        </div>
        {willBeDriver && (
          <div className="space-y-1">
            <Label htmlFor={`bd-${line.id}`} className="text-[11px]">Driver</Label>
            <NativeSelect id={`bd-${line.id}`} className="h-8 w-[280px] text-xs" value={driverId} onChange={(e) => setDriverId(e.target.value)}>
              <option value="">{options.loading ? 'Loading drivers…' : 'Choose the driver'}</option>
              {line.driver_id && !drivers.some((d) => d.driver_id === line.driver_id) && (
                <option value={line.driver_id}>{line.driver_name ?? 'Current driver'}</option>
              )}
              {drivers.map((d) => (
                <option key={d.driver_id} value={d.driver_id}>
                  {d.driver_name ?? 'Unnamed'}, {d.shifts} shift{d.shifts === 1 ? '' : 's'}{d.on_the_day ? ', on it that day' : ''}
                </option>
              ))}
            </NativeSelect>
          </div>
        )}
        <div className="min-w-[240px] flex-1 space-y-1">
          <Label htmlFor={`br-${line.id}`} className="text-[11px]">Why</Label>
          <Input id={`br-${line.id}`} className="h-8 text-xs" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={willBeDriver ? 'Reversed into a pole at the rank, reported by the driver on 12 September' : 'The insurer is paying for this panel'} />
        </div>
        <Button size="sm" className="h-8" onClick={save} disabled={set.loading}>
          {set.loading && <Spinner className="mr-1 h-3.5 w-3.5" />} Save
        </Button>
      </div>
      {willBeDriver && (
        <p className="text-[11px] text-muted-foreground">
          Driver borne opens a driver repair case for the driver you name. It does not deduct anything: the case
          needs a fault finding, a fair hearing and the driver's written agreement first.
          {!options.loading && drivers.length === 0 && ' Nobody drove this car in the two months around this line, so there is no one to suggest.'}
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

function InvoiceUpload({ vehicleId, period, lines, defaultCategory }: {
  vehicleId: string; period: string; lines: BreakdownLine[]; defaultCategory: string | null
}) {
  const [open, setOpen] = useState(false)
  const [category, setCategory] = useState(defaultCategory ?? '')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const fileRef = useRef<HTMLInputElement>(null)
  const upload = useUploadMaintenanceInvoice<{ error?: string; filed?: boolean }>()
  const candidates = useMemo(() => lines.filter((l) => l.vehicle_id === vehicleId && monthStart(l.period_start) === monthStart(period)), [lines, vehicleId, period])

  async function send() {
    const f = fileRef.current?.files?.[0]
    if (!f) { toast.error('Choose the invoice file'); return }
    if (f.size > 10 * 1024 * 1024) { toast.error('That file is larger than 10 MB. Save it again at a smaller size.'); return }
    try {
      const contentBase64 = await fileToBase64(f)
      const r = await upload.trigger({
        vehicle_id: vehicleId, period_start: period, proposed_category: category || null,
        line_ids: [...picked], filename: f.name, contentType: f.type, contentBase64,
      }).result
      if (r?.error) {
        toast.warning('Invoice filed, but not read', { description: r.error })
      } else {
        toast.success('Invoice filed and read', { description: 'Check the reader’s suggestions below.' })
      }
      setOpen(false)
      setPicked(new Set())
      if (fileRef.current) fileRef.current.value = ''
    } catch (err) {
      toast.error('Could not file the invoice', { description: writeErrorMessage(err) })
    }
  }

  if (!open) {
    return (
      <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => setOpen(true)}>
        <Upload className="h-3.5 w-3.5" /> File an invoice for this car and month
      </Button>
    )
  }
  return (
    <div className="space-y-2 rounded-md border border-border bg-background p-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="inv-file" className="text-[11px]">Invoice or slip (PDF or photo, up to 10 MB)</Label>
          <Input id="inv-file" ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="h-8 text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="inv-cat" className="text-[11px]">What you think it is for</Label>
          <CategorySelect id="inv-cat" value={category} onChange={setCategory} />
        </div>
      </div>
      {candidates.length > 0 && (
        <div className="space-y-1">
          <p className="text-[11px] text-muted-foreground">Tie it to the lines it pays for (the reader will also try to match each item):</p>
          <div className="flex flex-wrap gap-2">
            {candidates.map((l) => (
              <label key={l.id} className="flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px]">
                <input type="checkbox" checked={picked.has(l.id)} onChange={(e) => {
                  setPicked((cur) => {
                    const next = new Set(cur)
                    if (e.target.checked) next.add(l.id); else next.delete(l.id)
                    return next
                  })
                }} />
                {categoryLabel(l.category)} {zarExact(l.total_incl_vat_zar)}
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" className="h-8" onClick={send} disabled={upload.loading}>
          {upload.loading ? <><Spinner className="mr-1 h-3.5 w-3.5" /> Filing and reading…</> : 'File and read'}
        </Button>
        <Button size="sm" variant="ghost" className="h-8" onClick={() => setOpen(false)} disabled={upload.loading}>Cancel</Button>
      </div>
    </div>
  )
}

function InvoiceCard({ invoice, lines, canEdit }: { invoice: BreakdownInvoice; lines: BreakdownLine[]; canEdit: boolean }) {
  const url = useGetMaintenanceInvoiceUrl<{ url: string }>()
  const reread = useReadMaintenanceInvoice<{ error?: string }>()
  const disagreements = invoice.items.filter((i) => !i.resolution && readerDisagrees(i)).length

  async function openFile() {
    try {
      const r = await url.trigger({ invoice_id: invoice.id }, { skipCache: true }).result
      window.open(r.url, '_blank', 'noopener')
    } catch (err) {
      toast.error('Could not open the file', { description: writeErrorMessage(err) })
    }
  }
  async function readAgain() {
    try {
      const r = await reread.trigger({ invoice_id: invoice.id }).result
      if (r?.error) toast.warning('Still not read', { description: r.error })
      else toast.success('Read again')
    } catch (err) {
      toast.error('Could not read the invoice', { description: writeErrorMessage(err) })
    }
  }

  return (
    <div className="rounded-md border border-border">
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/50 px-3 py-2 text-xs">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <span className="font-semibold text-foreground">{invoice.supplier_name ?? invoice.filename ?? 'Invoice'}</span>
        {invoice.invoice_number && <span className="text-muted-foreground">No. {invoice.invoice_number}</span>}
        {invoice.invoice_date && <span className="text-muted-foreground">{invoice.invoice_date}</span>}
        <span className="font-mono uppercase text-muted-foreground">{invoice.registration_number}</span>
        <span className="text-muted-foreground">{monthLabel(invoice.period_start)}</span>
        {invoice.source === 'cash_repair' && <Chip tone="neutral">Cash slip</Chip>}
        <span className="ml-auto font-semibold tabular-nums text-foreground">{zarExact(invoice.total_zar)}</span>
        {invoice.read_status === 'read'
          ? <Chip tone="green">Read</Chip>
          : invoice.read_status === 'failed' ? <Chip tone="red">Not read</Chip> : <Chip tone="amber">Waiting to be read</Chip>}
        {disagreements > 0 && <Chip tone="amber"><Sparkles className="mr-1 h-3 w-3" />{disagreements} suggestion{disagreements === 1 ? '' : 's'} to check</Chip>}
        {invoice.has_file && (
          <Button size="sm" variant="ghost" className="h-6 gap-1 px-2 text-[11px]" onClick={openFile}>
            <ExternalLink className="h-3 w-3" /> Open file
          </Button>
        )}
        {canEdit && invoice.read_status !== 'read' && (
          <Button size="sm" variant="ghost" className="h-6 gap-1 px-2 text-[11px]" onClick={readAgain} disabled={reread.loading}>
            {reread.loading ? <Spinner className="h-3 w-3" /> : <RefreshCw className="h-3 w-3" />} Read again
          </Button>
        )}
      </div>
      {invoice.read_error && (
        <p className="border-b border-border px-3 py-2 text-[11px] text-rose-700 dark:text-rose-300">{invoice.read_error}</p>
      )}
      {invoice.warnings.length > 0 && (
        <ul className="border-b border-border px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300">
          {invoice.warnings.map((w, i) => <li key={i}>Check: {w}</li>)}
        </ul>
      )}
      {invoice.items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-xs">
            <thead>
              <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-2 py-1.5 text-left font-semibold">Item</th>
                <th className="px-2 py-1.5 text-right font-semibold">Ex VAT</th>
                <th className="px-2 py-1.5 text-left font-semibold">Proposed</th>
                <th className="px-2 py-1.5 text-left font-semibold">Reader suggests</th>
                <th className="px-2 py-1.5 text-left font-semibold">Settle</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((it) => (
                <ItemRow key={it.id} item={it} invoice={invoice}
                  matched={lines.find((l) => l.id === it.matched_line_id) ?? null} canEdit={canEdit} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function ItemRow({ item, invoice, matched, canEdit }: {
  item: InvoiceItem; invoice: BreakdownInvoice; matched: BreakdownLine | null; canEdit: boolean
}) {
  const disagree = readerDisagrees(item)
  const [category, setCategory] = useState<string>(item.suggested_category ?? item.proposed_category ?? invoice.proposed_category ?? '')
  const resolve = useResolveMaintenanceInvoiceItem<{ borne_by?: string; resolution?: string }>()
  const amount = item.amount_ex_vat_zar
  const wholeLine = matched && item.amount_ex_vat_zar !== null && Math.abs(Number(matched.amount_ex_vat_zar) - Number(item.amount_ex_vat_zar)) < 0.01

  async function act(action: 'keep' | 'move' | 'split' | 'record', cat: string | null) {
    try {
      const r = await resolve.trigger({ item_id: item.id, action, category: cat }).result
      const verb = action === 'keep' ? 'Kept as proposed' : action === 'record' ? 'Recorded as a line' : action === 'move' ? 'Line moved' : 'Split out'
      toast.success(verb, { description: r?.borne_by ? `Who pays: ${borneLabel(r.borne_by)}.` : undefined })
    } catch (err) {
      toast.error('Could not settle the item', { description: writeErrorMessage(err) })
    }
  }

  return (
    <tr className={cn('border-b border-border/60 align-top', disagree && !item.resolution && 'bg-amber-50 dark:bg-amber-950/30')}>
      <td className="max-w-[260px] px-2 py-1.5 text-foreground">
        {item.description}
        {matched && <div className="text-[10px] text-muted-foreground">Part of the {categoryLabel(matched.category)} line, {zarExact(matched.amount_ex_vat_zar)} ex VAT</div>}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">
        {amount === null
          ? <span className="text-muted-foreground" title="The reader could not find an ex VAT figure for this item">
              {item.amount_incl_vat_zar !== null ? `${zarExact(item.amount_incl_vat_zar)} incl` : 'Not found'}
            </span>
          : zarExact(amount)}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5">{item.proposed_category ? categoryLabel(item.proposed_category) : <span className="text-muted-foreground">None</span>}</td>
      <td className="max-w-[280px] px-2 py-1.5">
        {item.suggested_category ? (
          <>
            <span className={cn('font-medium', disagree ? 'text-amber-800 dark:text-amber-200' : 'text-foreground')}>
              {categoryLabel(item.suggested_category)}
            </span>
            {item.confidence && <span className="ml-1 text-[10px] text-muted-foreground">{item.confidence} confidence</span>}
            {!disagree && item.proposed_category && <span className="ml-1 text-[10px] text-emerald-700 dark:text-emerald-300">agrees</span>}
            {item.suggested_reason && <div className="text-[10px] text-muted-foreground">{item.suggested_reason}</div>}
          </>
        ) : <span className="text-muted-foreground">No suggestion</span>}
      </td>
      <td className="px-2 py-1.5">
        {item.resolution ? (
          <Chip tone="neutral">{item.resolution === 'kept' ? 'Kept' : item.resolution === 'moved' ? 'Line moved' : item.resolution === 'split' ? 'Split out' : 'Recorded'}</Chip>
        ) : !canEdit ? null : matched ? (
          disagree && item.suggested_category ? (
            <div className="flex flex-wrap gap-1">
              {wholeLine ? (
                <Button size="sm" variant="secondary" className="h-6 px-2 text-[11px]" disabled={resolve.loading}
                  onClick={() => act('move', item.suggested_category)}>
                  Move line to {categoryLabel(item.suggested_category)}
                </Button>
              ) : (
                <Button size="sm" variant="secondary" className="h-6 px-2 text-[11px]" disabled={resolve.loading || item.amount_ex_vat_zar === null}
                  onClick={() => act('split', item.suggested_category)}>
                  Split {zarExact(item.amount_ex_vat_zar)} to {categoryLabel(item.suggested_category)}
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" disabled={resolve.loading}
                onClick={() => act('keep', null)}>
                Keep {categoryLabel(item.proposed_category)}
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" disabled={resolve.loading} onClick={() => act('keep', null)}>
              Confirm
            </Button>
          )
        ) : (
          <div className="flex flex-wrap items-center gap-1">
            <NativeSelect className="h-6 w-[150px] text-[11px]" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Category</option>
              {MAINTENANCE_CATEGORIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </NativeSelect>
            <Button size="sm" variant="secondary" className="h-6 px-2 text-[11px]"
              disabled={resolve.loading || !category || item.amount_ex_vat_zar === null}
              title={item.amount_ex_vat_zar === null ? 'No ex VAT figure was found for this item; record it by hand with Record spend.' : undefined}
              onClick={() => act('record', category)}>
              Record as line
            </Button>
          </div>
        )}
      </td>
    </tr>
  )
}
